import { ProseMirror } from "@progress/kendo-react-editor";
import { handleEditorError } from "../hooks";

const { keymap, liftListItem, sinkListItem } = ProseMirror;

export interface KeyboardShortcutsPluginOptions {
  indentMargin?: number;
}

const DEFAULT_OPTIONS: Required<KeyboardShortcutsPluginOptions> = {
  indentMargin: 30 // Default margin for paragraph indentation in pixels
};
/**
 * Handles indent/outdent actions for both list items and regular paragraphs
 */
const handleIndentAction = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  view: any,
  action: "indent" | "outdent",
  options: Required<KeyboardShortcutsPluginOptions>
): boolean => {
  try {
    if (!view || !state || !dispatch) {
      return false;
    }

    const { selection } = state;
    const { $from } = selection;
    const tr = state.tr;

    // Check if we're inside a list item
    const listItemType = state.schema.nodes.list_item;

    // Find if cursor is in a list item
    let listItemNode = null;
    let listItemPos = null;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type === listItemType) {
        listItemNode = node;
        listItemPos = $from.before(depth);
        break;
      }
    }

    if (listItemNode && listItemPos !== null) {
      // We're in a list item, handle list-specific indentation
      // Store the current selection position
      const currentSelection = selection;

      if (action === "indent") {
        // Try to sink (increase nesting level) the list item
        if (sinkListItem && listItemType) {
          const command = sinkListItem(listItemType);

          // Create a custom dispatch that preserves selection position
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const customDispatch = (tr: any) => {
            if (dispatch) {
              dispatch(tr);

              // After the transaction, try to restore the selection position
              setTimeout(() => {
                try {
                  if (view && view.state) {
                    const newState = view.state;
                    const newDoc = newState.doc;

                    // Try to find a similar position to where we were
                    let targetPos = Math.min(
                      currentSelection.from,
                      newDoc.content.size - 1
                    );
                    targetPos = Math.max(1, targetPos);

                    const newSelection = newState.selection.constructor.near(
                      newState.doc.resolve(targetPos)
                    );

                    const selectionTr = newState.tr.setSelection(newSelection);
                    view.dispatch(selectionTr);
                  }
                } catch (error) {
                  // Ignore selection restoration errors
                  handleEditorError(error as Error, "selection restoration");
                }
              }, 0);
            }
          };

          const result = command(state, customDispatch);
          if (result) {
            return true;
          }
        }
      } else {
        // Try to lift (decrease nesting level) the list item
        if (liftListItem && listItemType) {
          const command = liftListItem(listItemType);

          // Create a custom dispatch that preserves selection position
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const customDispatch = (tr: any) => {
            if (dispatch) {
              dispatch(tr);

              // After the transaction, try to restore the selection position
              setTimeout(() => {
                try {
                  if (view && view.state) {
                    const newState = view.state;
                    const newDoc = newState.doc;

                    // Try to find a similar position to where we were
                    let targetPos = Math.min(
                      currentSelection.from,
                      newDoc.content.size - 1
                    );
                    targetPos = Math.max(1, targetPos);

                    const newSelection = newState.selection.constructor.near(
                      newState.doc.resolve(targetPos)
                    );

                    const selectionTr = newState.tr.setSelection(newSelection);
                    view.dispatch(selectionTr);
                  }
                } catch (error) {
                  // Ignore selection restoration errors
                  handleEditorError(error as Error, "selection restoration");
                }
              }, 0);
            }
          };

          const result = command(state, customDispatch);
          if (result) {
            return true;
          }
        }
      }

      // If we got here, the list commands didn't work, so return false
      return false;
    } else {
      // We're not in a list, handle regular paragraph indentation
      if (action === "indent") {
        // Add margin-left to indent
        const currentStyle = $from.parent.attrs.style || "";
        const currentMargin = currentStyle.includes("margin-left")
          ? parseInt(
              currentStyle.match(/margin-left:\s*(\d+)px/)?.[1] || "0",
              10
            )
          : 0;
        const newMargin = currentMargin + options.indentMargin;

        const newStyle = currentStyle.includes("margin-left")
          ? currentStyle.replace(
              /margin-left:\s*\d+px/,
              `margin-left: ${newMargin}px`
            )
          : `${currentStyle}${
              currentStyle ? " " : ""
            }margin-left: ${newMargin}px;`;

        tr.setNodeMarkup($from.before($from.depth), null, {
          ...$from.parent.attrs,
          style: newStyle
        });
      } else {
        // Remove or reduce margin-left to outdent
        const currentStyle = $from.parent.attrs.style || "";
        const currentMargin = currentStyle.includes("margin-left")
          ? parseInt(
              currentStyle.match(/margin-left:\s*(\d+)px/)?.[1] || "0",
              10
            )
          : 0;

        if (currentMargin > 0) {
          const newMargin = Math.max(0, currentMargin - options.indentMargin);
          const newStyle =
            newMargin > 0
              ? currentStyle.replace(
                  /margin-left:\s*\d+px/,
                  `margin-left: ${newMargin}px`
                )
              : currentStyle.replace(/\s*margin-left:\s*\d+px;?\s*/, "");

          tr.setNodeMarkup($from.before($from.depth), null, {
            ...$from.parent.attrs,
            style: newStyle.trim()
          });
        }
      }
    }

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }
  } catch (error) {
    handleEditorError(error as Error, `${action} keyboard shortcut`);
  }

  return false;
};

/**
 * Creates the keyboard shortcuts plugin with Tab/Shift-Tab support
 */
export const createKeyboardShortcutsPlugin = (
  options: KeyboardShortcutsPluginOptions = {}
) => {
  const pluginOptions = { ...DEFAULT_OPTIONS, ...options };

  return keymap({
    Tab: (state, dispatch, view) =>
      handleIndentAction(state, dispatch, view, "indent", pluginOptions),
    "Shift-Tab": (state, dispatch, view) =>
      handleIndentAction(state, dispatch, view, "outdent", pluginOptions)
  });
};

/**
 * Utility function to check if keyboard shortcuts plugin is active in the current state
 */
export const isKeyboardShortcutsPluginActive = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any
): boolean => {
  try {
    if (!state?.plugins) return false;

    return state.plugins.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (plugin: any) =>
        plugin?.spec?.props?.handleKeyDown &&
        plugin?.key?.indexOf?.("keymap") !== -1
    );
  } catch (error) {
    handleEditorError(error as Error, "keyboard shortcuts plugin detection");
    return false;
  }
};
